import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  RefreshCw,
  Search,
  Server,
  Wand2,
  Zap,
} from 'lucide-react';
import { usePaletteStore } from '../../stores/paletteStore';
import { ACCENT } from '../../styles/tokens';
import { useClusterStore } from '../../stores/clusterStore';
import { useSelectedIdStore } from '../../stores/selectedIdStore';
import { useEscalarModal } from '../modals/EscalarModalProvider';
import { useClusterFlow } from '../../hooks/useClusterFlow';
import { getFlow } from '../../lib/api';
import { toast } from '../../lib/toast';

type ItemType = 'action' | 'cluster' | 'node' | 'service';

interface PaletteItem {
  type: ItemType;
  id: string;
  label: string;
  hint: string;
  icon: ReactNode;
  onPick: () => void;
}

const TYPE_LABEL: Record<ItemType, string> = {
  action: 'AÇÃO',
  cluster: 'CLUSTER',
  node: 'NODE',
  service: 'SERVIÇO',
};

const SECTION_PATH = {
  services: '/services',
  nodes: '/nodes',
} as const;

export function CommandPalette() {
  const open = usePaletteStore(s => s.open);
  const query = usePaletteStore(s => s.query);
  const setQuery = usePaletteStore(s => s.setQuery);
  const close = usePaletteStore(s => s.close);
  const activeSlug = useClusterStore(s => s.activeSlug);
  const clusters = useClusterStore(s => s.clusters);
  const setActive = useClusterStore(s => s.setActive);
  const setPending = useSelectedIdStore(s => s.setPending);
  const { openEscalar } = useEscalarModal();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const enabled = open && activeSlug !== 'all';
  const flowStream = useClusterFlow(enabled ? activeSlug : null);
  const flowFallback = useQuery({
    queryKey: ['flow', activeSlug],
    queryFn: () => getFlow(activeSlug),
    enabled,
    staleTime: 30_000,
  });

  const flow = flowStream.data?.flow ?? flowFallback.data;

  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries();
    toast('Sincronização disparada', { tone: 'info' });
  }, [queryClient]);

  const handleEscalar = useCallback(() => {
    openEscalar({
      type: 'bulk',
      cluster: activeSlug === 'all' ? '' : activeSlug,
      items: [],
    });
  }, [openEscalar, activeSlug]);

  const items = useMemo<PaletteItem[]>(() => {
    const actions: PaletteItem[] = [
      {
        type: 'action',
        id: 'escalar',
        label: 'Escalar máquinas…',
        hint: 'Abrir modal de escalonamento',
        icon: <Wand2 size={13} />,
        onPick: () => {
          handleEscalar();
        },
      },
      {
        type: 'action',
        id: 'refresh',
        label: 'Sincronizar agora',
        hint: 'Refresh global',
        icon: <RefreshCw size={13} />,
        onPick: () => {
          handleRefresh();
        },
      },
      {
        type: 'action',
        id: 'suggest',
        label: 'Sugerir redistribuição automática',
        hint: 'Em breve · IA recomenda distribuição',
        icon: <Zap size={13} />,
        onPick: () => {
          toast('Sugerir distribuição: em breve', { tone: 'info' });
        },
      },
    ];

    const clusterItems: PaletteItem[] = clusters.map(c => ({
      type: 'cluster',
      id: c.slug,
      label: c.name,
      hint: `cluster · ${c.slug}`,
      icon: <Server size={13} />,
      onPick: () => {
        setActive(c.slug);
        toast(`Cluster ${c.name} selecionado`, { tone: 'info' });
      },
    }));

    const nodeItems: PaletteItem[] = (flow?.nodes ?? []).slice(0, 4).map(n => ({
      type: 'node',
      id: n.id,
      label: n.hostname,
      hint: `${n.ip} · ${n.machines}/${n.machineAmount} máq`,
      icon: <Server size={13} />,
      onPick: () => {
        setPending('nodes', n.id);
        navigate(SECTION_PATH.nodes);
      },
    }));

    const serviceItems: PaletteItem[] = (flow?.services ?? [])
      .slice(0, 6)
      .map(s => ({
        type: 'service',
        id: s.id,
        label: s.name,
        hint: `${s.stack} · ${s.system}`,
        icon: <Grid size={13} />,
        onPick: () => {
          setPending('services', s.id);
          navigate(SECTION_PATH.services);
        },
      }));

    return [...actions, ...clusterItems, ...nodeItems, ...serviceItems];
  }, [
    clusters,
    flow,
    handleEscalar,
    handleRefresh,
    navigate,
    setActive,
    setPending,
  ]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      i => i.label.toLowerCase().includes(q) || i.hint.toLowerCase().includes(q)
    );
  }, [items, query]);

  const handlePick = useCallback(
    (item: PaletteItem) => {
      item.onPick();
      close();
    },
    [close]
  );

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight(h => Math.min(filtered.length - 1, h + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight(h => Math.max(0, h - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const picked = filtered[highlight];
        if (picked) handlePick(picked);
      }
    },
    [filtered, highlight, handlePick, close]
  );

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const onBackdropClick = (e: React.MouseEvent): void => {
    if (e.target === e.currentTarget) close();
  };

  const body = (
    <div
      data-testid="command-palette-backdrop"
      onClick={onBackdropClick}
      onKeyDown={onKeyDown}
      className="fixed inset-0 z-[60] flex justify-center bg-primary/30 backdrop-blur pt-20"
    >
      <div
        data-testid="command-palette"
        role="dialog"
        aria-label="Paleta de comandos"
        onClick={e => e.stopPropagation()}
        className="flex max-h-[70vh] w-[540px] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-xl bg-surface shadow-[0_30px_80px_rgba(15,23,42,.25)]"
      >
        <div className="flex items-center gap-2 border-b border-borderSoft px-3.5 py-3">
          <Search size={15} className="text-textMute" />
          <input
            ref={inputRef}
            data-testid="command-palette-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar nodes, serviços ou comandos…"
            className="h-6 flex-1 border-none bg-transparent p-0 font-sans text-sm text-text outline-none focus:ring-0"
          />
          <kbd className="rounded border border-border bg-surfaceAlt px-1.5 py-px font-mono text-[9.5px] text-textMute">
            esc
          </kbd>
        </div>

        <div className="flex-1 overflow-auto py-1.5">
          {filtered.length === 0 && (
            <div
              data-testid="command-palette-empty"
              className="px-3.5 py-8 text-center text-[12px] text-textMute"
            >
              Nenhum resultado para &quot;{query}&quot;
            </div>
          )}
          {filtered.slice(0, 12).map((item, i) => {
            const isActive = i === highlight;
            const typeLabel = TYPE_LABEL[item.type];
            return (
              <button
                key={`${item.type}-${item.id}`}
                type="button"
                data-testid={`palette-item-${item.type}-${item.id}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => handlePick(item)}
                className={`flex w-full items-center gap-2.5 border-none px-3.5 py-2 text-left transition-colors ${
                  isActive
                    ? 'bg-accentSoft'
                    : 'bg-transparent hover:bg-accentSoft'
                }`}
              >
                <span
                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md"
                  style={
                    item.type === 'action'
                      ? { background: ACCENT, color: '#fff' }
                      : { background: '#f7f7f8', color: '#6b7280' }
                  }
                >
                  {item.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block truncate text-[13px] font-semibold text-text ${
                      item.type !== 'action' ? 'font-mono' : 'font-sans'
                    }`}
                  >
                    {item.label}
                  </span>
                  <span className="mt-0.5 block text-[10.5px] text-textMute">
                    {item.hint}
                  </span>
                </span>
                <span className="rounded bg-surfaceAlt px-1.5 py-px font-mono text-[9px] font-bold tracking-wider text-textMute">
                  {typeLabel}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex gap-3.5 border-t border-borderSoft bg-surfaceAlt px-3.5 py-2 font-mono text-[10px] text-textMute">
          <span>
            <kbd className="rounded border border-border bg-surface px-1 py-px text-[9px]">
              ↑↓
            </kbd>{' '}
            navegar
          </span>
          <span>
            <kbd className="rounded border border-border bg-surface px-1 py-px text-[9px]">
              ↵
            </kbd>{' '}
            selecionar
          </span>
          <span className="flex-1" />
          <span data-testid="palette-result-count">
            {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}

export default CommandPalette;
